var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
// Presets refer to presets of the video/audio device. We'll use an audio channel to select presets.
const debug = false;
const allowedMuteTypes = ['audio', 'master', 'multiview', 'window'];
const allowedVolumeTypes = ['audio', 'master'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: [...allowedMuteTypes, 'preset'],
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: [...allowedMuteTypes, 'preset'],
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: [],
    receiveGain: [],
    soundReinforcementMute: [...allowedMuteTypes, 'preset'],
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
function isAlfatronAudioAudioChannelConfig(config) {
    return typeof config === 'string';
}
class Channel {
    constructor(type, socket, key, name, log) {
        this.type = type;
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.socket = socket;
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    matches(key) {
        return this.key === key;
    }
}
class AudioChannel extends Channel {
    constructor(socket, key, name, log) {
        super('audio', socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            const cmd = `q ${inOrOut} ${chan} audio vol!`;
            const match = yield this.socket.send(cmd, new RegExp(`@outp\\s+audio\\s+${chan},?\\s*vol(?:ume)?\\s*(\\d+)`), tags);
            return parseInt(match[1], 10);
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            const cmd = `q ${inOrOut} ${chan} audiomute!`;
            const match = yield this.socket.send(cmd, new RegExp(`@${inOrOut}\\s+audiomute\\s+${chan},?\\s*(\\d+)`), tags);
            return match[1] === '1';
        });
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            const cmd = `s ${inOrOut} ${chan} audio vol ${value}!`;
            const match = yield this.socket.send(cmd, new RegExp(`@${inOrOut}\\s+audio\\s+${chan},?\\s+vol(?:ume)?\\s*(\\d+)`), tags);
            return parseInt(match[1], 10);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            const cmd = `s ${inOrOut} ${chan} audiomute ${value ? '1' : '0'}!`;
            const match = yield this.socket.send(cmd, new RegExp(`@${inOrOut}\\s+audiomute\\s+${chan},?\\s*(\\d+)`), tags);
            return match[1] === '1';
        });
    }
}
class MasterChannel extends Channel {
    constructor(socket, key, name, log) {
        super('master', socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `q master audio vol!`;
            const match = yield this.socket.send(cmd, /@master\s+audio\s+vol\s*(\d+)/i, tags);
            return parseInt(match[1], 10);
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `q master audio mute!`;
            const match = yield this.socket.send(cmd, /@master\s+audio\s*mute\s*(\d+)/i, tags);
            return match[1] === '1';
        });
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `s master audio vol ${value}!`;
            yield this.socket.send(cmd, /@master\s+audio\s*vol(?:ume)?\s*(\d+)/i, tags);
            return value;
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `set master audio mute ${value ? '1' : '0'}!`;
            yield this.socket.send(cmd, /@master\s+audio\s*mute\s*(\d+)/i, tags);
            return value;
        });
    }
}
// Select channels to route to a particular window. If a channel is unmuted, it is routed to that
// window.
class WindowChannel extends Channel {
    constructor(socket, key, name, log, state) {
        super('window', socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
        this.state = state;
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [, win, chan] = this.key.split('.');
            const cmd = `q window ${win}!`;
            const match = yield this.socket.send(cmd, new RegExp(`@window\\s+in\\s*${win},?\\s*(\\d+)`), tags);
            return !(match[1] === chan); // if the routed channel is this channel, then it is unmuted
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!value) {
                // unmute, i.e., route input to the window
                const [, win, chan] = this.key.split('.');
                const cmd = `s window ${win} in ${chan}!`;
                const match = yield this.socket.send(cmd, new RegExp(`@window\\s+in\\s*${win},?\\s*${chan}`), tags);
                if (match) {
                    // Set mute for other source selections for this window. There are 5 windows: 0 for
                    // all, then 1-4.
                    const chanNum = parseInt(chan, 10);
                    for (let i = 0; i <= 4; i++) {
                        if (i !== chanNum)
                            this.state.mutes.update(`window.${win}.${i}`, true);
                    }
                }
            }
            return value;
        });
    }
}
// Select a layout for the multiviewer as an audio channel. Unmuted means the layout is selected.
class MultiviewChannel extends Channel {
    constructor(socket, key, name, log, state) {
        super('multiview', socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
        this.state = state;
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [, layout] = this.key.split('.');
            const cmd = `q multiview!`;
            const match = yield this.socket.send(cmd, new RegExp(`@multiview\\s+(\\d+)`), tags);
            return !(match[1] === layout); // mute = false when layout is the selected
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!value) {
                // unmute, i.e., select the layout
                const [, layout] = this.key.split('.');
                const cmd = `s multiview ${layout}!`;
                const match = yield this.socket.send(cmd, new RegExp(`@multiview\\s*${layout}`), tags);
                // clear state of other layouts as mutes
                if (match) {
                    const layoutNum = parseInt(layout, 10);
                    for (let i = 1; i <= 5; i++) {
                        if (i !== layoutNum) {
                            this.state.mutes.update(`multiview.${i}`, true);
                        }
                    }
                }
            }
            return value;
        });
    }
}
class PresetChannel extends Channel {
    constructor(socket, key, name, log, state) {
        super('preset', socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
        this.state = state;
    }
    // Presets will show as off (muted) unless they have been recalled.
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return true;
        });
    }
    // Muting the channel recalls the preset.
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!value) {
                const [, chan] = this.key.split('.');
                const cmd = `s recall ps ${chan}!`;
                const match = yield this.socket.send(cmd, new RegExp(`@recall\\s+ps\\s+${chan}`), tags);
                // clear state of other presets as mutes
                if (match) {
                    const chanNum = parseInt(chan, 10);
                    for (let i = 1; i < 5; i++) {
                        if (i !== chanNum) {
                            this.state.mutes.update(`preset.${i}`, true);
                        }
                    }
                }
            }
            return value;
        });
    }
}
class Channels {
    constructor(log) {
        this.log = log;
        this.channels = [];
    }
    addChannel(chan) {
        if (debug) {
            this.log.debug(`chan.type = ${chan.type}, name: ${chan.name}`);
        }
        this.channels.push(chan);
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    clearChannels() {
        this.channels.length = 0;
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: false,
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getAllConfiguredChannels() {
        return this.channels.filter(channel => channel.isMuteConfigured || channel.isVolumeConfigured);
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getChannelType(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel === null || channel === void 0 ? void 0 : channel.type;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                type: channel.type,
                name: channel.name,
                filters: this.computeFilters(channel.type),
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
}
export class AlfatronSck51 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'q power!',
                next: /@power\s*(on|off|\d)/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`q fw version!`, /MCU\s*([\d\.]+)/i, tags);
                return match[1] ? match[1] : 'unknown';
            }),
            powerOffRestrictions: ['get'],
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getMute(tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                const muteResult = yield channel.setMute(value, tags);
                return muteResult !== null && muteResult !== void 0 ? muteResult : this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    // audio input and outputs
                    regExp: /@(inp|outp)\s+audio\s*mute\s*(\d+),?\s*(\d+)/i,
                    callback: (match) => {
                        const key = `${match[1].toLowerCase()}.${match[2]}`;
                        const value = match[3] === '1';
                        this.state.mutes.update(key, value);
                    },
                },
                {
                    // master audio channel
                    regExp: /@master\s+audio\s*mute\s*(\d+)/i,
                    callback: (match) => {
                        const key = `master.1`;
                        const value = match[1] === '1';
                        this.state.mutes.update(key, value);
                    },
                },
                {
                    //  multiview selection
                    regExp: /@multiview\s*(\d+)/i,
                    callback: (match) => {
                        const key = `multiview.${match[1]}`;
                        this.state.mutes.update(key, false);
                        // Mute the other multiview channels to indicate they are not selected.
                        const chanNum = parseInt(match[1], 10);
                        for (let i = 1; i <= 5; i++) {
                            if (i !== chanNum) {
                                this.state.mutes.update(`multiview.${i}`, true);
                            }
                        }
                    },
                },
                {
                    // window routing
                    regExp: /@window\s+in\s*(\d+),?\s*(\d+)/i,
                    callback: (match) => {
                        const win = match[1];
                        const key = `window.${win}.${match[2]}`;
                        this.state.mutes.update(key, false);
                        // Mute the other window source channels to indicate they are not selected.
                        const chanNum = parseInt(match[2], 10);
                        for (let i = 1; i <= 5; i++) {
                            if (i !== chanNum) {
                                this.state.mutes.update(`window.${win}.${i}`, true);
                            }
                        }
                    },
                },
                {
                    // preset recall
                    regExp: /@recall\s+ps\s+(\d+)/i,
                    callback: (match) => {
                        const key = `preset.${match[1]}`;
                        this.state.mutes.update(key, false);
                        const chanNum = parseInt(match[1], 10);
                        for (let i = 1; i <= 5; i++) {
                            if (i !== chanNum) {
                                this.state.mutes.update(`preset.${i}`, true);
                            }
                        }
                    },
                },
            ],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`q power!`, /@power\s+(\d)/i, tags);
                return match[1] === '1';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`s power ${power ? '1' : '0'}!`, /@power\s+(\d)/i, tags, { sendTimeoutMs: 6000 } // ack upon power on takes longer
                );
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 20000 : 1000);
                // Takes about 20 seconds after power up before it accepts commands again.
                this.framework.suspend(power ? 20000 : 1000);
                return match[1] === '1';
            }),
            asyncHandlers: [
                {
                    regExp: /@power\s+(\d)/i,
                    callback: (match) => {
                        this.state.power.update(match[1] === '1');
                    },
                },
            ],
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`q inp source!`, /@inp\s+source\s+(\d+)/i, tags);
                return parseInt(match[1], 10).toString(); // remove leading 0's
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`s inp source ${value}!`, /@inp\s+source\s+(\d+)/i, tags);
                return parseInt(match[1], 10).toString(); // remove any leading 0's
            }),
            asyncHandlers: [
                {
                    regExp: /@inp\s+source\s+(\d+)/i,
                    callback: (match) => {
                        const value = parseInt(match[1], 10).toString();
                        this.state.videoMatrixRoutes.update('1', value); // there is only one output
                    },
                },
            ],
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getFader(tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.setFader(value, tags);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 6, min: -18, round: 1 };
                return { max: 6, min: -44, round: 1 };
            }),
            asyncHandlers: [
                {
                    regExp: /@(inp|outp)\s+audio\s+(\d),?\s+vol(?:ume)?\s*(\d+)/i,
                    callback: (match) => {
                        const key = `${match[1].toLowerCase()}.${match[2]}`;
                        const value = parseInt(match[3], 10);
                        this.state.volumes.update(key, value);
                    },
                },
                {
                    regExp: /@master\s+audio\s+vol(?:ume)?\s*(\d+)/i,
                    callback: (match) => {
                        const key = `master.1`;
                        const value = parseInt(match[1], 10);
                        this.state.volumes.update(key, value);
                    },
                },
            ],
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['power', 'videoMatrixRoutes'],
            },
        ];
        this.channels = new Channels(log);
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.channels.clearChannels();
            // Configure auto switching.
            yield this.socket.send(`s autoswitch ${(config === null || config === void 0 ? void 0 : config.autoSwitchVideoInputs) ? '1' : '0'}!`, /@autoswitch\s*(\d)/i, ['configure']);
            const inputNames = ['All', 'Main (HDMI/USB-C)', 'Line', 'Dante'];
            const outputNames = ['All', 'HDMI/HDBT', 'Line', 'Dante'];
            for (let i = 0; i <= 3; i++) {
                this.channels.addChannel(new AudioChannel(this.socket, `inp.${i}`, `Input: ${inputNames[i]}`, this.log));
                this.channels.addChannel(new AudioChannel(this.socket, `outp.${i}`, `Output: ${outputNames[i]}`, this.log));
            }
            this.channels.addChannel(new MasterChannel(this.socket, `master.1`, 'Master Channel', this.log));
            for (let i = 1; i <= 5; i++) {
                // Get the preset name.
                let match;
                try {
                    match = yield this.socket.send(`q ps ${i} name!`, /@ps\s*(\d+)\s+name:?\s*(.+)/i, [
                        `preset ${i}`,
                    ]);
                }
                catch (error) { }
                this.channels.addChannel(new PresetChannel(this.socket, `preset.${i}`, `Preset: ${match && match[2] ? match[2] : 'Preset ' + i}`, this.log, this.state));
            }
            // Create the sources routed to window as "audio" channels. Unmuting routes the source to
            // the window.
            const srcNames = ['USB-C 1', 'USB-C 2', 'HDMI 3', 'HDMI 4', 'HDMI 5'];
            for (let win = 0; win <= 4; win++) {
                for (let chan = 1; chan <= 5; chan++) {
                    this.channels.addChannel(new WindowChannel(this.socket, `window.${win}.${chan}`, `Window ${win === 0 ? ' (All)' : ` ${win}`}: Input ${chan} (${srcNames[chan - 1]})`, this.log, this.state));
                }
            }
            // Create the 5 multiview layouts as "audio" channels. Unmuting selects the layout.
            const mvLayoutNames = ['Single', 'PIP', 'PBP', 'Triple', 'Quad'];
            for (let i = 1; i <= 5; i++) {
                this.channels.addChannel(new MultiviewChannel(this.socket, `multiview.${i}`, `Multiview: ${mvLayoutNames[i - 1]}`, this.log, this.state));
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    getKey(config) {
        if (isAlfatronAudioAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (isAlfatronAudioAudioChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    // Check if volume mute can be supported by the channel.
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
AlfatronSck51.channelsConfigName = 'eclerVidaControlChannels';
AlfatronSck51.driverType = 'socketDriver';
AlfatronSck51.key = '-OJsAOoSgSahwm92bP9t';
AlfatronSck51.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r',
};
AlfatronSck51.socketOptions = [
    Object.assign(Object.assign({}, AlfatronSck51.baseSocketOptions), { port: 8000 }),
    Object.assign(Object.assign({}, AlfatronSck51.baseSocketOptions), { port: 4999 }),
];
